import { createRoot, hydrateRoot } from "react-dom/client";
import App from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";
import {
  ClerkStartupGate,
  ClerkStartupProvider,
} from "./components/ClerkStartupGate";
import { PwaUpdatePrompt } from "./components/PwaUpdatePrompt";
import "./index.css";
import type { PublicLegalDocument } from "@shared/publicLegalDocument";

// Server-rendered public Vault page (/p/<slug>). When the server injected the
// owner's widget payload (window.__VAULT_DATA__) and the SSR'd template body into
// #root, hydrate ONLY the standalone Vault template here — it has no
// Clerk/wouter/TanStack dependencies, so it needs none of the app providers. This
// preserves the server markup for crawlers and avoids a fetch flash. Any other
// page (or a failed/absent SSR) falls through to the normal SPA boot below.
type VaultData = {
  slug: string;
  payload: unknown;
  salesMode?: boolean;
  customDomain?: boolean;
  legalDocument?: PublicLegalDocument;
};
const vaultData = (window as unknown as { __VAULT_DATA__?: VaultData }).__VAULT_DATA__;

// Activate the Google Fonts stylesheet (loaded non-render-blocking with
// media="print" in index.html). Done here in bundled JS because the prod CSP
// (script-src-attr 'none') blocks inline onload handlers. First paint has
// already happened by the time this runs, so the flip never blocks render;
// display=swap handles the font swap-in.
document.getElementById("gfonts-css")?.setAttribute("media", "all");

const STALE_CHUNK_RECOVERY_KEY = "__incontrol_stale_chunk_recovery";
const STALE_CHUNK_RECOVERY_WINDOW_MS = 10_000;
let staleChunkReloadScheduled = false;

function isStaleDynamicImportFailure(value: unknown): boolean {
  const message =
    value instanceof Error
      ? value.message
      : typeof value === "string"
        ? value
        : value && typeof value === "object" && "message" in value
          ? String((value as { message?: unknown }).message ?? "")
          : "";

  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed")
  );
}

function recoverFromStaleDynamicImport(): boolean {
  // Vite can report the same failed import through preloadError, an unhandled
  // rejection, and window.error in the same page. Once this page has scheduled
  // its reload, suppress those duplicate reports instead of letting Replit
  // mark an otherwise recovering workflow as failed.
  if (staleChunkReloadScheduled) return true;
  try {
    const previousAttempt = Number(
      window.sessionStorage.getItem(STALE_CHUNK_RECOVERY_KEY) ?? "0",
    );
    const now = Date.now();
    if (Number.isFinite(previousAttempt) && now - previousAttempt < STALE_CHUNK_RECOVERY_WINDOW_MS) {
      // Do not trap a user in a refresh loop if the chunk is genuinely
      // unavailable.
      return false;
    }
    staleChunkReloadScheduled = true;
    window.sessionStorage.setItem(STALE_CHUNK_RECOVERY_KEY, String(now));
    const url = new URL(window.location.href);
    url.searchParams.set("__vite_reload", String(now));
    window.location.replace(url.toString());
    return true;
  } catch {
    // Storage can be unavailable in hardened/private browser contexts. The
    // normal error logging below still makes the underlying failure visible.
    return false;
  }
}

if (typeof window !== "undefined") {
  // Vite emits this dedicated event before a failed lazy import reaches React.
  // Handling it here prevents the runtime-error overlay from leaving the
  // Replit preview on a blank shell after the optimized-dependency cache changes
  // across workflow restarts.
  window.addEventListener("vite:preloadError", (event) => {
    if (recoverFromStaleDynamicImport()) event.preventDefault();
  });
  window.addEventListener("unhandledrejection", (event) => {
    if (isStaleDynamicImportFailure(event.reason)) {
      if (recoverFromStaleDynamicImport()) {
        event.preventDefault();
        return;
      }
    }
    console.error("[unhandledrejection]", event.reason);
  });
  window.addEventListener("error", (event) => {
    if (isStaleDynamicImportFailure(event.error ?? event.message)) {
      if (recoverFromStaleDynamicImport()) {
        event.preventDefault();
        return;
      }
    }
    console.error("[window.error]", event.error || event.message);
  });

  // Silence noisy React warning caused by the Replit cartographer dev plugin
  // injecting `data-replit-metadata` onto JSX fragments (which only accept
  // `key` and `children`). The plugin is dev-only, so this filter is too.
  if (import.meta.env.DEV) {
    const origError = console.error.bind(console);
    console.error = (...args: unknown[]) => {
      const first = args[0];
      if (
        typeof first === "string" &&
        first.includes("React.Fragment can only have") &&
        args.some(
          (a) => typeof a === "string" && a.includes("data-replit-metadata"),
        )
      ) {
        return;
      }
      origError(...args);
    };
  }
}

const rootEl = document.getElementById("root")!;

const receptionistEmbedMatch = window.location.pathname.match(
  /^\/p\/([^/]+)\/receptionist-embed\/?$/,
);

if (vaultData?.payload) {
  // Lazy-load the Vault template chunk (it's already a separate bundle via the
  // App's lazy route) and hydrate the server-rendered markup in place.
  import("./vault-template/VaultTemplatePage").then(({ default: VaultTemplatePage }) => {
    hydrateRoot(
      rootEl,
      <ErrorBoundary>
        <VaultTemplatePage
          slug={vaultData.slug}
          initialPayload={vaultData.payload}
          salesMode={vaultData.salesMode === true}
          customDomain={vaultData.customDomain === true}
          legalDocument={vaultData.legalDocument}
        />
      </ErrorBoundary>,
    );
  });
} else if (receptionistEmbedMatch) {
  // The legal-page chat iframe is a fully public surface. Boot it directly so
  // its launcher cannot be held behind Clerk startup (or fail when auth is
  // temporarily unavailable) on an otherwise healthy public legal page.
  const slug = decodeURIComponent(receptionistEmbedMatch[1]);
  import("./vault-template/VaultTemplatePage").then(({ ReceptionistEmbedPage }) => {
    createRoot(rootEl).render(
      <ErrorBoundary>
        <ReceptionistEmbedPage slug={slug} />
      </ErrorBoundary>,
    );
  });
} else {
  createRoot(rootEl).render(
    <ErrorBoundary>
      <ClerkStartupGate>
        <ClerkStartupProvider>
          <App />
        </ClerkStartupProvider>
      </ClerkStartupGate>
      {/* PWA app-shell offline support — registered only on the SPA entry path,
          never on the public Vault hydration branch above. */}
      <PwaUpdatePrompt />
    </ErrorBoundary>
  );
}
